// src/redux/RTK/OragnizationsQuerys/organisation.js
import { baseApi } from "../Api";
import { ORGANIZATION_EMPLOYEE_ROLES, ORGANIZATION_ALL_EMPLOYEE_ROLES, USER_PRIVILEGES, REQUEST_KIND_MENU_OPTIONS, ORGANIZATION_UPDATE_EMPLOYEE_PROPERTIES, SHORTCUTS_LIST, SHORTCUTS_TOGGLE, NOTIFICATION_GET_MY, NOTIFICATION_READ, NOTIFICATION_READ_ALL, COMPANY_GET_ALL, COMPANY_GET, COMPANY_GET_TENANT_CONFIG, COMPANY_CREATE, COMPANY_UPDATE, COMPANY_DELETE, COMPANY_BULK_DELETE, COMPANY_IMAGE_UPLOAD, MOBILE_GET_COMPANY_CONFIG, MOBILE_SAVE_COMPANY_CONFIG, NOTIFICATION_TYPE_DROPDOWN, NOTIFICATION_ASSIGN_TYPE_DROPDOWN } from "./Endpoints";

export const organizationApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    // GET Tenant Config for Azure login
    getCompanyTenantConfig: build.query({
      query: (code) => ({
        url: `${COMPANY_GET_TENANT_CONFIG}?code=${encodeURIComponent(code)}`,
        method: 'GET',
      }),
    }),

    // POST Companies (list)
    getAllCompanies: build.mutation({
      query: (body) => ({
        url: COMPANY_GET_ALL,
        method: 'POST',
        data: body,
      }),
      providesTags: (result) =>
        result
          ? [{ type: 'Company', id: 'LIST' }]
          : [{ type: 'Company', id: 'LIST' }],
    }),

    // GET Company by ID
    getCompanyById: build.query({
      query: (id) => ({
        url: `${COMPANY_GET}/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'Company', id }],
    }),

    // POST Create Company
    createCompany: build.mutation({
      query: (body) => ({
        url: COMPANY_CREATE,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: [{ type: 'Company', id: 'LIST' }],
    }),

    // PUT Update Company
    updateCompany: build.mutation({
      query: ({ id, body }) => ({
        url: `${COMPANY_UPDATE}/${id}`,
        method: 'PUT',
        data: body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Company', id },
        { type: 'Company', id: 'LIST' },
      ],
    }),

    // DELETE Company
    deleteCompany: build.mutation({
      query: (id) => ({
        url: `${COMPANY_DELETE}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Company', id: 'LIST' }],
    }),
    
    // POST Upload Company Image (multipart/form-data)
    uploadCompanyImage: build.mutation({
      query: ({ code, imageFile, isUpdate = false, isReportLogo = false }) => {
        const formData = new FormData();
        if (code) formData.append('Code', code);
        if (imageFile) formData.append('Image', imageFile, imageFile.name || 'image.png');
        formData.append('IsUpdate', isUpdate ? 'true' : 'false');
        formData.append('IsReportLogo', isReportLogo ? 'true' : 'false');
        
        return {
          url: COMPANY_IMAGE_UPLOAD,
          method: 'POST',
          data: formData, // axiosBaseQuery handles FormData correctly
        };
      },
    }),

    // DELETE Bulk Companies
    bulkDeleteCompany: build.mutation({
      query: (body) => ({
        url: COMPANY_BULK_DELETE,
        method: 'DELETE',
        data: body,
      }),
      invalidatesTags: [{ type: 'Company', id: 'LIST' }],
    }),

    // GET example (already present)
    getEmployeeRoles: build.query({
      query: (companyID) => ({
        url: `${ORGANIZATION_EMPLOYEE_ROLES}?companyID=${companyID}`,
        method: 'GET',
      }),
    }),
    
    // ... rest of endpoints
    getAllEmployeeRoles: build.query({
      query: () => ({
        url: `${ORGANIZATION_ALL_EMPLOYEE_ROLES}`,
        method: 'GET',
      }),
    }),

    // POST example
    createEmployeeRole: build.mutation({
      query: ({ companyID, body }) => ({
        url: `${ORGANIZATION_EMPLOYEE_ROLES}?companyID=${companyID}`,
        method: 'POST',
        data: body, // important: 'data' is the axios body in your axiosBaseQuery
      }),
      // optional: auto-refresh role lists if you enable tagTypes ['EmployeeRoles']
      // invalidatesTags: (result, error, { companyID }) => [
      //   { type: 'EmployeeRoles', id: companyID ?? 'LIST' },
      // ],
    }),

    getUserPrivileges: build.query({
      query: ({ companyID, assignRoleKey, roleId }) => {
        const resolvedKey = assignRoleKey ?? roleId ?? null;
        return {
          url: USER_PRIVILEGES,
          method: 'GET',
          params: { companyID, assignRoleKey: resolvedKey === null ? 'null' : resolvedKey },
        };
      },
    }),

    getRequestKindMenuOptions: build.query({
      query: ({companyID, lookupCode }) => ({
        url: `${REQUEST_KIND_MENU_OPTIONS}?companyID=${companyID}&code=${lookupCode}`,
        method: 'GET',
      }),
    }),

    // Notification Type Dropdown for Workflow
    getNotificationTypeDropdown: build.query({
      query: ({ code = 'WORKFLOW', body }) => ({
        url: `${NOTIFICATION_TYPE_DROPDOWN}?code=${encodeURIComponent(code)}`,
        method: 'POST',
        data: body,
      }),
    }),

    // Notification Assign Type Dropdown for Company onboarding welcome message
    getNotificationAssignTypeDropdown: build.query({
      query: ({ body } = {}) => ({
        url: `${NOTIFICATION_ASSIGN_TYPE_DROPDOWN}`,
        method: 'POST',
        data: body || {},
      }),
      providesTags: ['Company'],
      keepUnusedDataFor: 600,
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
      refetchOnReconnect: false,
    }),

    // Update employee configuration/properties
    updateEmployeeProperties: build.mutation({
      query: (body) => ({
        url: ORGANIZATION_UPDATE_EMPLOYEE_PROPERTIES,
        method: 'POST',
        data: body,
      }),
    }),

    // Shortcuts (dashboard pins)
    getEmployeeShortcuts: build.query({
      query: ({ employeeID, companyID }) => ({
        url: `${SHORTCUTS_LIST}?employeeID=${employeeID}&companyID=${companyID}`,
        method: 'GET',
      }),
    }),

    toggleEmployeeShortcut: build.mutation({
      query: ({ employeeID, body }) => ({
        url: `${SHORTCUTS_TOGGLE}?employeeID=${employeeID}`,
        method: 'POST',
        data: body,
      }),
    }),

    getMyNotifications: build.query({
      query: (body) => ({
        url: NOTIFICATION_GET_MY,
        method: 'POST',
        data: body,
      }),
    }),

    readNotification: build.mutation({
      query: (body) => ({
        url: NOTIFICATION_READ,
        method: 'POST',
        data: body,
      }),
    }),

    readAllNotifications: build.mutation({
      query: (body) => ({
        url: NOTIFICATION_READ_ALL,
        method: 'POST',
        data: body,
      }),
    }),

    getCompanyMobileConfig: build.query({
      query: ({ companyID }) => ({
        url: MOBILE_GET_COMPANY_CONFIG,
        method: 'GET',
        params: { companyID },
      }),
    }),

    saveCompanyMobileConfig: build.mutation({
      query: ({ companyID, body }) => ({
        url: MOBILE_SAVE_COMPANY_CONFIG,
        method: 'POST',
        params: { companyID },
        data: body,
      }),
    }),
  }),
});

export const {
  useGetCompanyTenantConfigQuery,
  useLazyGetCompanyTenantConfigQuery,
  useGetAllCompaniesMutation,
  useGetCompanyByIdQuery,
  useLazyGetCompanyByIdQuery,
  useCreateCompanyMutation,
  useUpdateCompanyMutation,
  useDeleteCompanyMutation,
  useBulkDeleteCompanyMutation,
  useUploadCompanyImageMutation,
  useGetEmployeeRolesQuery,
  useGetAllEmployeeRolesQuery,
  useCreateEmployeeRoleMutation,  
  useGetUserPrivilegesQuery,
  useLazyGetUserPrivilegesQuery,
  useGetRequestKindMenuOptionsQuery,
  useGetNotificationTypeDropdownQuery,
  useGetNotificationAssignTypeDropdownQuery,
  useUpdateEmployeePropertiesMutation,
  useGetEmployeeShortcutsQuery,
  useToggleEmployeeShortcutMutation,
  useLazyGetMyNotificationsQuery,
  useReadNotificationMutation,
  useReadAllNotificationsMutation,
  useGetCompanyMobileConfigQuery,
  useLazyGetCompanyMobileConfigQuery,
  useSaveCompanyMobileConfigMutation,
} = organizationApi;
//   useCreateEmployeeRoleMutation



// Below is example for use case of POST METHOD 

// import { useCreateEmployeeRoleMutation } from '@/redux/RTK/OragnizationsQuerys/organisation';
// import { getCompanyID } from '@/helpers/Utils';

// function AddRoleButton() {
//   const companyID = getCompanyID();
//   const [createRole, { isLoading, error, isSuccess }] = useCreateEmployeeRoleMutation();

//   const handleCreate = async () => {
//     const payload = { name: 'New Role', permissions: ['read', 'write'] };
//     try {
//       // unwrap throws on error and returns data on success
//       const res = await createRole({ companyID, body: payload }).unwrap();
//       console.log('Created', res);
//       // Optionally: manually refetch or rely on invalidatesTags if enabled
//       // dispatch(baseApi.util.invalidateTags([{ type: 'EmployeeRoles', id: companyID }]));
//     } catch (e) {
//       console.error('Create failed', e);
//     }
//   };

//   return (
//     <button onClick={handleCreate} disabled={isLoading}>
//       {isLoading ? 'Saving...' : 'Add Role'}
//     </button>
//   );
// }